import React, { useEffect, useMemo, useRef, useState } from 'react';

interface NavigationProps {
  activeView: string;
  onViewChange: (view: string) => void;
}

const Navigation: React.FC<NavigationProps> = ({ activeView, onViewChange }) => {
  const views = useMemo(
    () => [
      { id: 'dashboard', label: 'Tableau de bord', icon: '📊' },
      { id: 'list', label: 'Liste de courses', icon: '🛒' },
      { id: 'planner', label: 'Planificateur de repas', icon: '📅' }
    ],
    []
  );

  const navRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  useEffect(() => {
    const activeIndex = views.findIndex((view) => view.id === activeView);
    const activeButton = buttonRefs.current[activeIndex];
    const navContainer = navRef.current;

    if (activeButton && navContainer) {
      const navRect = navContainer.getBoundingClientRect();
      const buttonRect = activeButton.getBoundingClientRect();
      setIndicatorStyle({
        left: buttonRect.left - navRect.left,
        width: buttonRect.width,
      });
    }
  }, [activeView, views]);

  return (
    <nav className="flex justify-center mb-8">
      <div
        ref={navRef}
        className="relative inline-flex items-center gap-2 bg-white rounded-full shadow-medium p-2"
      >
        {/* Sliding active indicator with 3D effect */}
        <div
          className="absolute top-2 bottom-2 rounded-full bg-primary/90 shadow-lg transition-all duration-500 ease-out backdrop-blur-sm pointer-events-none"
          style={{
            left: `${indicatorStyle.left}px`,
            width: `${indicatorStyle.width}px`,
            transform: 'translateZ(10px)',
            boxShadow: '0 6px 20px rgba(0, 123, 255, 0.4), 0 2px 8px rgba(0, 0, 0, 0.15)',
          }}
        />

        {/* Hover indicator with 3D effect */}
        {hoveredIndex !== null && hoveredIndex !== views.findIndex((v) => v.id === activeView) && (
          <div
            className="absolute top-2 bottom-2 rounded-full bg-primary/20 shadow-md transition-all duration-300 ease-out pointer-events-none"
            style={{
              left: `${buttonRefs.current[hoveredIndex]?.offsetLeft || 0}px`,
              width: `${buttonRefs.current[hoveredIndex]?.offsetWidth || 0}px`,
              transform: 'translateZ(5px)',
              boxShadow: '0 4px 12px rgba(0, 123, 255, 0.2)',
            }}
          />
        )}

        {views.map((view, index) => {
          const isActive = activeView === view.id;
          return (
            <button
              key={view.id}
              ref={(el) => {
                buttonRefs.current[index] = el;
              }}
              type="button"
              onClick={() => onViewChange(view.id)}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              className={`
                relative flex items-center gap-2 px-6 py-3 rounded-full
                font-semibold text-sm transition-all duration-300 ease-in-out z-10
                ${isActive ? 'text-white' : 'text-gray-700 hover:text-primary'}
              `}
            >
              <span
                className={`text-lg transition-all duration-300 ease-in-out ${
                  isActive ? 'scale-110' : ''
                }`}
              >
                {view.icon}
              </span>
              <span className="transition-all duration-300">{view.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default Navigation;